import type { CompetitorMax, GridRow } from "./api";
import { humanDate, money, signedPct } from "./format";
import type { ViewLookup } from "./order_views";

// c1-competition-cell (bug b85a3efc): the Competition cell and the drawer's feed lines read what C2 serves, verbatim (INV-12).
export const COMPETITION_COPY = {
  label: "Price vs competitor max",
  older: "Not served by this API yet",
  noFigure: "No figure",
  meta: (reference: string | null, currency: string | null, count: number | null, observed: string | null) =>
    `max ${money(reference, currency)} · ${count ?? "?"} competitors · ${humanDate(observed)}`,
};
export const TCM_COPY = {
  fed: "Fed",
  missing: "Missing",
  older: "Not on this grid",
  avg_discount: "28-day average per unit, fed daily; part of TRUE contribution",
  avg_refund: "90-day average per unit, fed daily; part of TRUE contribution",
  none: { avg_discount: "no discount cost fed for this SKU", avg_refund: "no refund cost fed for this SKU" },
};
export type TcmInput = "avg_discount" | "avg_refund";

function viewMax(lookup: ViewLookup | undefined): CompetitorMax | null | undefined {
  if (!lookup || lookup.kind === "off") return undefined;
  if (lookup.kind !== "row" || !lookup.row.figures) return null;
  return lookup.row.figures.metrics.vs_competitor_max_pct ?? null;
}

/** The cell's figure: the view row's in an order-type view, else the grid row's; null when C2 serves none. */
export function competitionFigure(row: GridRow, lookup?: ViewLookup): string | null {
  const inView = viewMax(lookup);
  const m = inView !== undefined ? inView : row.competition?.vs_competitor_max_pct ?? null;
  return m?.value == null ? null : signedPct(m.value);
}

/** Why the cell has no figure, as C2 names it; null when it has one or a view row names nothing. */
export function competitionReason(row: GridRow, lookup?: ViewLookup): string | null {
  const inView = viewMax(lookup);
  if (inView !== undefined) return inView && inView.value == null ? inView.reason : null;
  const m = row.competition?.vs_competitor_max_pct;
  if (!m) return COMPETITION_COPY.older;
  return m.value == null ? m.reason : null;
}

/** The drawer's Competition lens line. */
export function competitionLine(row: GridRow): { value: string; meta: string; muted: boolean } {
  const m = row.competition?.vs_competitor_max_pct;
  if (!m) return { value: COMPETITION_COPY.noFigure, meta: COMPETITION_COPY.older, muted: true };
  if (m.value == null) return { value: COMPETITION_COPY.noFigure, meta: m.reason ?? COMPETITION_COPY.older, muted: true };
  return { value: signedPct(m.value), meta: COMPETITION_COPY.meta(m.reference, row.price?.currency ?? row.currency, m.competitor_count, m.observed_at), muted: false };
}

/** The drawer's Avg discount / Avg refund line: fed or missing, from the inputs TRUE contribution names as missing (C4 v1.18). */
export function tcmInputLine(row: GridRow, input: TcmInput): { value: string; meta: string; muted: boolean } {
  const list = row.margins?.true_contribution_missing;
  if (!Array.isArray(list)) return { value: TCM_COPY.older, meta: TCM_COPY[input], muted: true };
  const hit = list.find((m) => m === input || m.startsWith(`${input}:`) || m.startsWith(`${input}_`));
  if (hit === undefined) return { value: TCM_COPY.fed, meta: TCM_COPY[input], muted: false };
  const detail = hit === input ? TCM_COPY.none[input] : hit.slice(input.length).replace(/^[:_]\s*/, "").replace(/_/g, " ");
  return { value: TCM_COPY.missing, meta: detail, muted: true };
}
